<template>
  <b-modal ref="modalRef" size="lg" centered :title="title" @hidden="onHidden">
    <template v-if="confirmingDisable">
      <p>
        Are you sure you want to disable <strong>{{ pot?.name }}</strong
        >?
      </p>
      <p class="text-muted mb-0">
        {{ pot?.holder?.full_name }} will no longer be able to give instant awards with a reward. You can re-enable the
        budget later from the Budgets tab.
      </p>
      <small v-if="errorMessage" class="text-danger d-block mt-3">{{ errorMessage }}</small>
    </template>

    <ValidationObserver v-else ref="observerRef" v-slot="{ handleSubmit }">
      <b-form id="pot-form" @submit.prevent="handleSubmit(onSubmit)">
        <p v-if="!isEdit" class="pot-modal__note">
          A manager budget gives one person a budget for instant awards. They can give up to the amount each period, and
          it shows in their app under Give recognition.
        </p>

        <div v-if="isEdit && pot" class="pot-modal__usage">
          <div class="pot-modal__usage-headline">
            {{ formatAmount(pot.spent_minor) }} of {{ formatAmount(pot.amount_minor) }} spent this
            {{ budgetPeriodNoun(pot.budget_period) }}
          </div>
          <budget-usage-bar :budget="pot" />
          <div class="pot-modal__usage-meta">
            <span v-if="pot.period_end_at">Renews {{ formatDate(pot.period_end_at) }}</span>
            <span>Spent {{ formatAmount(pot.spent_minor) }}</span>
            <span>Remaining {{ formatAmount(pot.remaining_minor) }}</span>
            <b-badge v-if="!pot.is_active" pill variant="secondary">Disabled</b-badge>
          </div>
        </div>

        <template v-if="isEdit">
          <app-input
            :value="holderLabel"
            name="Holder"
            label="Holder"
            description="To change the holder, disable this budget and create a new one."
            plaintext
          />
        </template>
        <b-form-group v-else label="Holder" label-for="pot-holder-search" class="pot-modal__holder">
          <div v-if="holder" class="pot-modal__holder-selected">
            <div>
              <div class="pot-modal__holder-name">{{ holder.full_name }}</div>
              <div class="pot-modal__holder-email">{{ holder.work_email }}</div>
            </div>
            <b-button variant="link" size="sm" class="p-0" @click="holder = null">Change</b-button>
          </div>
          <employee-search v-else input-id="pot-holder-search" placeholder="Search by name" @select="selectHolder" />
          <small v-if="holderError" class="text-danger">{{ holderError }}</small>
        </b-form-group>

        <app-input
          v-model="name"
          :validate-immediate="false"
          name="Budget name"
          label="Budget name"
          placeholder="e.g. Front of house spot awards"
          rules="required|max:100"
        />

        <budget-amount-fields
          :amount="amount"
          :period="period"
          :currency-symbol="currencySymbol"
          :period-helper="periodHelper"
          @update:amount="amount = $event"
          @update:period="period = $event"
        />

        <small v-if="errorMessage" class="text-danger d-block mt-2">{{ errorMessage }}</small>
      </b-form>
    </ValidationObserver>

    <template #modal-footer>
      <template v-if="confirmingDisable">
        <b-button variant="outline-secondary" :disabled="saving" @click="confirmingDisable = false"
          >Keep budget</b-button
        >
        <b-button variant="danger" :disabled="saving" @click="setActive(false)">
          <b-spinner v-if="saving" small class="mr-1" />
          Disable budget
        </b-button>
      </template>
      <template v-else>
        <div class="pot-modal__footer-left">
          <b-button
            v-if="isEdit && pot?.is_active"
            variant="outline-secondary"
            :disabled="saving"
            @click="confirmingDisable = true"
          >
            Disable budget
          </b-button>
          <b-button v-else-if="isEdit" variant="outline-secondary" :disabled="saving" @click="setActive(true)">
            Re-enable budget
          </b-button>
        </div>
        <b-button type="submit" variant="primary" form="pot-form" :disabled="saving">
          <b-spinner v-if="saving" small class="mr-1" />
          {{ isEdit ? 'Save changes' : 'Create budget' }}
        </b-button>
      </template>
    </template>
  </b-modal>
</template>

<script setup>
import { computed, ref } from 'vue';
import { ValidationObserver } from 'vee-validate';
import { useMutation } from '@tanstack/vue-query';
import AppInput from '@/components/AppInput.vue';
import EmployeeSearch from '@/pages/recognition/components/EmployeeSearch.vue';
import BudgetAmountFields from '@/pages/recognition/components/budgets/BudgetAmountFields.vue';
import BudgetUsageBar from '@/pages/recognition/components/budgets/BudgetUsageBar.vue';
import { createRecognitionBudget, updateRecognitionBudget } from '@/queries/recognition';
import {
  budgetPeriodNoun,
  majorToMinor,
  minorToMajorString,
  nextBudgetPeriodStart,
} from '@/pages/recognition/utils/budgets';
import { toCurrency } from '@/utils';
import { toFormattedDate } from '@/utils/date';
import { currencySymbolFromCode } from '@/utils/common';
import useToast from '@/composables/useToast';
import State from '@/state/State';

const emit = defineEmits(['saved']);

const { doToast, TOAST_VARIANT } = useToast();
const createPotMutation = useMutation(createRecognitionBudget());
const updatePotMutation = useMutation(updateRecognitionBudget());

const modalRef = ref(null);
const observerRef = ref(null);
const pot = ref(null);
const holder = ref(null);
const holderError = ref('');
const name = ref('');
const amount = ref('');
const period = ref(undefined);
const saving = ref(false);
const errorMessage = ref('');
const confirmingDisable = ref(false);

const isEdit = computed(() => Boolean(pot.value));
const title = computed(() => {
  if (confirmingDisable.value) {
    return 'Disable manager budget';
  }
  return isEdit.value ? 'Edit manager budget' : 'Create manager budget';
});

const currency = computed(() => State.state?.company?.default_currency);
const currencySymbol = computed(() => currencySymbolFromCode(currency.value));
const holderLabel = computed(() =>
  pot.value?.holder ? `${pot.value.holder.full_name} - ${pot.value.holder.work_email}` : '',
);

const formatAmount = minor => toCurrency(minor ?? 0, currency.value);
const formatDate = date => toFormattedDate(date, 'D MMM YYYY');

const periodHelper = computed(() => {
  if (isEdit.value) {
    return 'Period changes apply from the next renewal.';
  }
  const nextStart = nextBudgetPeriodStart(period.value);
  const renewal = nextStart ? ` and renews on ${formatDate(nextStart)}` : '';
  return `First period starts today${renewal}. Unspent budget does not roll over.`;
});

const selectHolder = employee => {
  holder.value = employee;
  holderError.value = '';
};

const show = (existingPot = null) => {
  pot.value = existingPot;
  holder.value = existingPot?.holder || null;
  name.value = existingPot?.name || '';
  amount.value = minorToMajorString(existingPot?.amount_minor);
  period.value = existingPot?.budget_period;
  errorMessage.value = '';
  holderError.value = '';
  confirmingDisable.value = false;
  modalRef.value?.show();
};

const onHidden = () => {
  pot.value = null;
  holder.value = null;
  observerRef.value?.reset();
};

const failureMessage = (e, fallback) => e?.variables?.message || e?.message || fallback;

const onSubmit = async () => {
  if (!isEdit.value && !holder.value) {
    holderError.value = 'Please choose who holds this budget.';
    return;
  }
  const payload = {
    name: name.value.trim(),
    amount_minor: majorToMinor(amount.value),
    budget_period: period.value,
  };
  saving.value = true;
  errorMessage.value = '';
  try {
    if (isEdit.value) {
      await updatePotMutation.mutateAsync({ budgetId: pot.value.recognition_budget_id, payload });
      doToast('Success', 'Manager budget updated', TOAST_VARIANT.SUCCESS);
    } else {
      await createPotMutation.mutateAsync({ ...payload, holder_employee_id: holder.value.employee_id });
      doToast('Success', 'Manager budget created', TOAST_VARIANT.SUCCESS);
    }
    emit('saved');
    modalRef.value?.hide();
  } catch (e) {
    errorMessage.value = failureMessage(e, 'Failed to save manager budget');
  } finally {
    saving.value = false;
  }
};

const setActive = async isActive => {
  saving.value = true;
  errorMessage.value = '';
  try {
    await updatePotMutation.mutateAsync({
      budgetId: pot.value.recognition_budget_id,
      payload: { is_active: isActive },
    });
    doToast('Success', `Manager budget ${isActive ? 're-enabled' : 'disabled'}`, TOAST_VARIANT.SUCCESS);
    emit('saved');
    modalRef.value?.hide();
  } catch (e) {
    errorMessage.value = failureMessage(e, `Failed to ${isActive ? 're-enable' : 'disable'} manager budget`);
  } finally {
    saving.value = false;
  }
};

defineExpose({ show });
</script>

<style scoped lang="scss">
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

:deep(.modal-footer) {
  border-top: 1px solid var(--palette-color-default-lighten-90);
}

.pot-modal {
  &__note {
    font-size: 0.8125rem;
    color: var(--palette-color-default-lighten-30);
  }

  &__usage {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.875rem 1rem;
    margin-bottom: 1.25rem;
    border-radius: 0.5rem;
    background-color: var(--palette-color-default-lighten-95);
  }

  &__usage-headline {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--palette-color-default);
  }

  &__usage-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem 1rem;
    font-size: 0.8125rem;
    color: var(--palette-color-default-lighten-30);
  }

  &__holder :deep(legend) {
    font-weight: 500;
    font-size: 0.875rem;
  }

  &__holder-selected {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--palette-color-default-lighten-90);
    border-radius: 0.5rem;
  }

  &__holder-name {
    font-weight: 500;
    font-size: 0.875rem;
  }

  &__holder-email {
    font-size: 0.8rem;
    color: var(--palette-color-default-lighten-30);
  }

  &__footer-left {
    margin-right: auto;
  }
}
</style>
